export type TrustFacet =
  | "kind"
  | "subject"
  | "capability"
  | "property"
  | "condition"
  | "state"
  | "authority"
  | "lane"
  | "invoke"
  | "focus"
  | "source"
  | "risk";

export type TrustRelation =
  | "is"
  | "about"
  | "has"
  | "requires"
  | "grants"
  | "blocks"
  | "invokes"
  | "produced-by"
  | "sourced-from";

export type TrustTagDefinition = Readonly<{
  id: string;
  label: string;
  displayName?: string;
  description: string;
  cost?: number;
  tier?: number;
  facet: TrustFacet;
  appliesTo: readonly string[];
  aliases: readonly string[];
  parentIds: readonly string[];
  families: readonly string[];
  requiredTags?: readonly string[];
  foundryTrainable?: boolean;
  sourceAuthority: string;
  schemaVersion: "deckos.trust.v1";
}>;

export type TrustTagAssignment = Readonly<{
  tagId: string;
  relation: TrustRelation;
  origin: string;
  confidence?: number;
  locked?: boolean;
  assignedAt?: string;
  assignedBy?: string;
  reason?: string;
}>;

export type TrustCatalog = Readonly<{
  get(id: string): TrustTagDefinition | undefined;
  resolve(alias: string): TrustTagDefinition | undefined;
  list(): readonly TrustTagDefinition[];
  getSourceAuthority(id: string): string | undefined;
}>;

export type TrustProjection = Readonly<{
  primarySubject?: TrustTagDefinition;
  state?: TrustTagDefinition;
  focus?: TrustTagDefinition;
  lanes: readonly TrustTagDefinition[];
  invokes: readonly TrustTagDefinition[];
  protectedAuthority: readonly TrustTagDefinition[];
}>;

export type TrustValidation = { ok: true } | { ok: false; reason: string };

const FACETS = new Set<TrustFacet>([
  "kind",
  "subject",
  "capability",
  "property",
  "condition",
  "state",
  "authority",
  "lane",
  "invoke",
  "focus",
  "source",
  "risk",
]);

const RELATIONS = new Set<TrustRelation>([
  "is",
  "about",
  "has",
  "requires",
  "grants",
  "blocks",
  "invokes",
  "produced-by",
  "sourced-from",
]);

const PROTECTED_AUTHORITY = new Set([
  "authority.imprinted",
  "authority.current-main",
  "authority.canon",
]);

const ALIASES = new Map<string, string>([
  ["kind.chat", "kind.chat"],
  ["chat", "kind.chat"],
  ["doc", "kind.document"],
  ["document", "kind.document"],
  ["task", "kind.task"],
  ["design", "kind.design"],
  ["build", "kind.build"],
  ["subject.deckos", "subject.deckos"],
  ["deckos", "subject.deckos"],
  ["subject.chipmap", "subject.chipmap"],
  ["chipmap", "subject.chipmap"],
  ["subject.arcopolis.core", "subject.arcopolis.core"],
  ["arcopolis core", "subject.arcopolis.core"],
  ["state.current", "state.current"],
  ["current", "state.current"],
  ["state.review", "state.review"],
  ["review", "state.review"],
  ["state.blocked", "state.blocked"],
  ["blocked", "state.blocked"],
  ["lane.core-authority", "lane.core-authority"],
  ["core authority", "lane.core-authority"],
  ["invoke.build", "invoke.build"],
  ["build", "invoke.build"],
  ["focus.now", "focus.now"],
  ["now", "focus.now"],
  ["source.local", "source.local"],
  ["local", "source.local"],
  ["risk.clear", "risk.clear"],
  ["clear", "risk.clear"],
]);

function normalizeId(value: string) {
  return value.trim().toLowerCase();
}

function isStringArray(value: unknown): value is readonly string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

function assertCatalogShape(definition: TrustTagDefinition) {
  if (!definition.id.trim()) throw new Error("Malformed TRUST catalog payload");
  if (!definition.label.trim())
    throw new Error("Malformed TRUST catalog payload");
  if (!definition.description.trim())
    throw new Error("Malformed TRUST catalog payload");
  if (!definition.sourceAuthority.trim())
    throw new Error("Malformed TRUST catalog payload");
  if (!isStringArray(definition.appliesTo))
    throw new Error("Malformed TRUST catalog payload");
  if (!isStringArray(definition.aliases))
    throw new Error("Malformed TRUST catalog payload");
  if (!isStringArray(definition.parentIds))
    throw new Error("Malformed TRUST catalog payload");
  if (!isStringArray(definition.families))
    throw new Error("Malformed TRUST catalog payload");
  if (definition.requiredTags && !isStringArray(definition.requiredTags))
    throw new Error("Malformed TRUST catalog payload");
}

export function createTrustCatalog(
  definitions: readonly TrustTagDefinition[],
): TrustCatalog {
  const byId = new Map<string, TrustTagDefinition>();
  const aliases = new Map<string, string>();
  for (const definition of definitions) {
    assertCatalogShape(definition);
    const id = normalizeId(definition.id);
    if (byId.has(id))
      throw new Error(`Duplicate TRUST tag id: ${definition.id}`);
    if (definition.schemaVersion !== "deckos.trust.v1")
      throw new Error(`Unsupported TRUST schema: ${definition.schemaVersion}`);
    if (!FACETS.has(definition.facet))
      throw new Error(`Invalid TRUST facet: ${definition.facet}`);
    byId.set(id, Object.freeze({ ...definition, id }));
    aliases.set(id, id);
    for (const alias of definition.aliases) {
      const key = normalizeId(alias);
      const existing = aliases.get(key);
      if (existing && existing !== id)
        throw new Error(`Ambiguous TRUST alias: ${alias}`);
      aliases.set(key, id);
    }
  }
  return Object.freeze({
    get(id: string) {
      return byId.get(normalizeId(id));
    },
    resolve(alias: string) {
      const key = normalizeId(alias);
      return byId.get(aliases.get(key) ?? ALIASES.get(key) ?? "");
    },
    list() {
      return Object.freeze([...byId.values()]);
    },
    getSourceAuthority(id: string) {
      return byId.get(normalizeId(id))?.sourceAuthority;
    },
  });
}

export function validateTrustDefinition(
  definition: TrustTagDefinition,
  catalog?: TrustCatalog,
): TrustValidation {
  if (!FACETS.has(definition.facet)) {
    return { ok: false, reason: `Invalid facet: ${definition.facet}` };
  }
  for (const relation of definition.appliesTo) {
    if (!RELATIONS.has(relation as TrustRelation)) {
      return { ok: false, reason: `Invalid relation: ${relation}` };
    }
  }
  if (catalog) {
    for (const alias of definition.aliases) {
      const existing = catalog.resolve(alias);
      if (existing && normalizeId(existing.id) !== normalizeId(definition.id)) {
        return { ok: false, reason: `Ambiguous alias: ${alias}` };
      }
    }
  }
  return { ok: true };
}

export function validateTrustAssignment(
  assignment: TrustTagAssignment,
  catalog: TrustCatalog,
): TrustValidation {
  const tag = catalog.resolve(assignment.tagId);
  if (!tag)
    return { ok: false, reason: `Unknown TRUST tag: ${assignment.tagId}` };
  if (!RELATIONS.has(assignment.relation))
    return { ok: false, reason: `Invalid relation: ${assignment.relation}` };
  if (tag.appliesTo.length > 0 && !tag.appliesTo.includes(assignment.relation))
    return { ok: false, reason: `Assignment not applicable for ${tag.id}` };
  return { ok: true };
}

export function isProtectedAuthorityTag(tagId: string) {
  return PROTECTED_AUTHORITY.has(normalizeId(tagId));
}

export function projectTrust(
  assignments: readonly TrustTagAssignment[],
  catalog: TrustCatalog,
): TrustProjection {
  const tags = assignments
    .map((assignment) => catalog.resolve(assignment.tagId))
    .filter((tag): tag is TrustTagDefinition => Boolean(tag));
  return {
    primarySubject: tags.find((tag) => tag.facet === "subject"),
    state: tags.find((tag) => tag.facet === "state"),
    focus: tags.find((tag) => tag.facet === "focus"),
    lanes: tags.filter((tag) => tag.facet === "lane"),
    invokes: tags.filter((tag) => tag.facet === "invoke"),
    protectedAuthority: tags.filter(
      (tag) => tag.facet === "authority" && isProtectedAuthorityTag(tag.id),
    ),
  };
}

export function serializeTrustCatalog(
  definitions: readonly TrustTagDefinition[],
) {
  return JSON.stringify(
    {
      schema: "deckos.trust.catalog/v1",
      tags: definitions.map((definition) => ({
        id: definition.id,
        displayName: definition.displayName ?? definition.label,
        description: definition.description,
        cost: definition.cost ?? 0,
        tier: definition.tier ?? 0,
        families: [...definition.families],
        requiredTags: [...(definition.requiredTags ?? [])],
        foundryTrainable: definition.foundryTrainable ?? false,
        facet: definition.facet,
        appliesTo: [...definition.appliesTo],
        aliases: [...definition.aliases],
        parentIds: [...definition.parentIds],
        sourceAuthority: definition.sourceAuthority,
        schemaVersion: definition.schemaVersion,
      })),
    },
    null,
    2,
  );
}

export function deserializeTrustCatalog(input: string): TrustTagDefinition[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch {
    throw new Error("Malformed TRUST catalog payload");
  }
  if (!parsed || typeof parsed !== "object")
    throw new Error("Malformed TRUST catalog payload");
  const value = parsed as Record<string, unknown>;
  if (!Array.isArray(value.tags))
    throw new Error("Malformed TRUST catalog payload");
  return value.tags.map((entry) => {
    if (!entry || typeof entry !== "object")
      throw new Error("Malformed TRUST catalog payload");
    const tag = entry as Record<string, unknown>;
    return {
      id: String(tag.id ?? ""),
      label: String(tag.displayName ?? tag.label ?? tag.id ?? ""),
      displayName: String(tag.displayName ?? tag.label ?? tag.id ?? ""),
      description: String(tag.description ?? ""),
      cost: Number(tag.cost ?? 0),
      tier: Number(tag.tier ?? 0),
      facet: (tag.facet ?? "subject") as TrustFacet,
      appliesTo: isStringArray(tag.appliesTo) ? [...tag.appliesTo] : [],
      aliases: isStringArray(tag.aliases) ? [...tag.aliases] : [],
      parentIds: isStringArray(tag.parentIds) ? [...tag.parentIds] : [],
      families: isStringArray(tag.families) ? [...tag.families] : [],
      requiredTags: isStringArray(tag.requiredTags)
        ? [...tag.requiredTags]
        : [],
      foundryTrainable: Boolean(tag.foundryTrainable ?? false),
      sourceAuthority: String(tag.sourceAuthority ?? "deckos.local"),
      schemaVersion: (tag.schemaVersion ??
        "deckos.trust.v1") as "deckos.trust.v1",
    } satisfies TrustTagDefinition;
  });
}

const OPERATIONS: TrustTagDefinition[] = [
  ["kind.chat", "Chat", "Chat surface", "kind"],
  ["kind.document", "Document", "Document surface", "kind"],
  ["kind.task", "Task", "Task surface", "kind"],
  ["kind.design", "Design", "Design surface", "kind"],
  ["kind.build", "Build", "Build surface", "kind"],
  ["subject.deckos", "DeckOS", "DeckOS subject", "subject"],
  ["subject.chipmap", "CHIPMAP", "CHIPMAP subject", "subject"],
  [
    "subject.arcopolis.core",
    "Arcopolis Core",
    "Core Arcopolis subject",
    "subject",
  ],
  ["subject.arcopolis.unity", "Arcopolis Unity", "Unity subject", "subject"],
  ["subject.arcopolis.lore", "Arcopolis Lore", "Lore subject", "subject"],
  ["state.current", "Current", "Current state", "state"],
  ["state.review", "Review", "Review state", "state"],
  ["state.blocked", "Blocked", "Blocked state", "state"],
  ["state.persistent", "Persistent", "Persistent state", "state"],
  ["state.dormant", "Dormant", "Dormant state", "state"],
  ["state.superseded", "Superseded", "Superseded state", "state"],
  ["lane.core-authority", "Core Authority", "Core authority lane", "lane"],
  [
    "lane.studio-world-source",
    "Studio Source",
    "Studio world source lane",
    "lane",
  ],
  [
    "lane.presentation-client",
    "Presentation Client",
    "Presentation client lane",
    "lane",
  ],
  ["lane.public-signal", "Public Signal", "Public signal lane", "lane"],
  ["lane.experiment-lab", "Experiment Lab", "Experiment lab lane", "lane"],
  [
    "lane.workflow-process",
    "Workflow Process",
    "Workflow process lane",
    "lane",
  ],
  ["invoke.loremine", "Loremine", "Loremine invoke", "invoke"],
  ["invoke.forge", "Forge", "Forge invoke", "invoke"],
  ["invoke.foundry", "Foundry", "Foundry invoke", "invoke"],
  ["invoke.artemis", "Artemis", "Artemis invoke", "invoke"],
  ["invoke.build", "Build", "Build invoke", "invoke"],
  ["invoke.verify", "Verify", "Verify invoke", "invoke"],
  ["invoke.arcopedia", "Arcopedia", "Arcopedia invoke", "invoke"],
  ["invoke.archive", "Archive", "Archive invoke", "invoke"],
  ["focus.now", "Now", "Current focus", "focus"],
  ["source.chatgpt", "ChatGPT", "ChatGPT source", "source"],
  ["source.codex", "Codex", "Codex source", "source"],
  ["source.github", "GitHub", "GitHub source", "source"],
  ["source.local", "Local", "Local source", "source"],
  ["source.unity", "Unity", "Unity source", "source"],
  ["risk.clear", "Clear", "Clear risk", "risk"],
  ["risk.review", "Review", "Review risk", "risk"],
  [
    "condition.missing-source",
    "Missing Source",
    "Missing source condition",
    "condition",
  ],
  [
    "condition.conflicting-authority",
    "Conflicting Authority",
    "Conflicting authority condition",
    "condition",
  ],
].map(([id, label, description, facet]) => ({
  id,
  label,
  displayName: label,
  description,
  facet: facet as TrustFacet,
  appliesTo: [],
  aliases: [id],
  parentIds: [],
  families: [],
  requiredTags: [],
  foundryTrainable: false,
  sourceAuthority: "deckos.local",
  schemaVersion: "deckos.trust.v1" as const,
}));

export const deckosTrustDefinitions = Object.freeze(OPERATIONS);
